import { useState } from 'react';
import { IconArrowLeft, IconBell, IconChevronDown, IconDots, IconMessage, IconX } from '@tabler/icons-react';
import { HeaderTool, Page, PageHeader } from '@hana/shell';
import { Button, useToast } from '@hana/ui';
import { useT } from '@hana/i18n';
import './payroll.i18n';
import { PAYROLL_ROWS, PAY_STEPS, calcPay, nf } from './payrollData';
import s from './payroll.module.css';

// Mint-Payroll `page=detail`: Phiếu lương một người trong kỳ — trang chi tiết 2:1 (D21-NB): trái Tóm tắt · Thu nhập · Khấu trừ & thực nhận · Hoạt động;
// phải 380: Việc cần làm · Thông tin chính · Các bước duyệt theo ghế · Người phụ trách · Liên kết. Chỉ xem; nút ghi báo toast. TIỀN → sau quyền `money` (cha đã chặn).

const PER = '10/2026';
const STEP = 2;
const SEAT: readonly (readonly [string, string])[] = [['[HRD-MGR · kiêm]', 'HANHLTM'], ['[HRD-MGR · kiêm]', 'HANHLTM'], ['[ACC-CA] trống → [BOD-DIR]', 'PHILIP'], ['[BOD-DIR]', 'PHILIP'], ['[ACC-CP]', 'HANHLTM'], ['Hệ thống', 'Hệ thống']];

const BON: Record<string, readonly [string, number]> = { NhiTHT: ['Thưởng xử lý amend gấp', 0.5], THANHTTK: ['Thưởng trực sự cố 21/Oct', 0.4] };

type Sec = 'sum' | 'inc' | 'ded' | 'act' | 'todo' | 'info' | 'steps' | 'ppl' | 'links';

function Card({ id, title, count, open, toggle, children }: { id: Sec; title: string; count?: string; open: boolean; toggle: (k: Sec) => void; children: React.ReactNode }) {
  return (
    <section className={s.sc} aria-label={title}>
      <div className={s.scH}>
        <h2>{title}</h2>
        {count && <span className={s.scN}>{count}</span>}
        <button type="button" className={s.scT} aria-expanded={open} aria-label={`Mở hoặc gập ${title}`} onClick={() => toggle(id)}>
          <IconChevronDown size={18} aria-hidden="true" style={{ transform: open ? 'rotate(180deg)' : 'none' }} />
        </button>
      </div>
      {open && <div className={s.scB}>{children}</div>}
    </section>
  );
}

const ini = (u: string) => u.replace(/[^A-Za-z0-9]/g, '').slice(0, 2).toUpperCase();

export function SlipPage({ user, onBack }: { user: string; onBack: () => void }) {
  const t = useT();
  const toast = useToast();
  const noWrite = () => toast({ msg: t('pr.noWrite') });
  const [closed, setClosed] = useState<Partial<Record<Sec, boolean>>>({ links: true });
  const toggle = (k: Sec) => setClosed((c) => ({ ...c, [k]: !c[k] }));
  const open = (k: Sec) => !closed[k];
  const row = PAYROLL_ROWS.find((r) => r[0] === user) ?? PAYROLL_ROWS[0]!;
  const c = calcPay(row);
  const alwRaw = row[6];
  const duty = row[7];
  const ded = c.ins + c.pit + c.adv + c.oth;
  const warn = c.cong === 0 ? 'công thực tế 0/22' : '';
  const bon = BON[c.u];
  const att = c.bonus - (bon?.[1] ?? 0) > 0.001 ? c.bonus - (bon?.[1] ?? 0) : 0;
  const inc = ([
    ['Lương chính theo công', c.main, `${nf(c.cong).replace(',0', '')}/${c.std}`, true],
    ['Phụ cấp trách nhiệm', c.resp, '', false],
    ['Phụ cấp ăn trưa · ĐT · xăng', c.cong ? alwRaw : 0, '', false],
    ['Phụ cấp kiêm nhiệm', c.cong ? duty : 0, duty ? 'kiêm nhiệm' : '', false],
    ['Lương chặng', c.leg, c.leg ? '2 chuyến' : '', false],
    ['Làm thêm giờ', c.ot, '', false],
    ['KPI', c.kpi, '', false],
    ['Chuyên cần', att, '', false],
  ] as const).filter((x) => x[1] || x[3]);
  const dedRows = [['BHXH 8%', (c.ins * 8) / 10.5, ''], ['BHYT 1,5%', (c.ins * 1.5) / 10.5, ''], ['BHTN 1%', c.ins / 10.5, ''], ['Thuế TNCN', c.pit, 'lũy tiến'], ['Tạm ứng', c.adv, ''], ['Khấu trừ khác', c.oth, '']] as const;
  const bar = ([['Thực nhận', c.net, 'var(--d-blue)'], ['BH NLĐ', c.ins, 'var(--d-violet)'], ['TNCN + tạm ứng + khác', c.pit + c.adv + c.oth, 'var(--d-amber)']] as const).filter((x) => x[1] > 0);
  const sum = [['Gross', nf(c.gross), ''], ['Khấu trừ', `−${nf(ded)}`, 'tx2'], ['Thực nhận', nf(c.net), 'net']] as const;
  const todo = [
    ...(warn ? [{ k: warn, who: 'HANHLTM', due: 'trước KTT kiểm' }] : []),
    ...(bon ? [{ k: 'Xác nhận 1 khoản thưởng thêm', who: 'HANHLTM', due: 'trước KTT kiểm' }] : []),
    { k: `KTT kiểm kỳ ${PER}`, who: 'PHILIP', due: 'đang chờ' },
  ];
  const act = [
    ['Tính lương · Gross ' + nf(c.gross) + ' tr', 'Hệ thống', '01/Nov/2026 09:05', 'Tính'],
    ['Chốt công ' + PER + ' · ' + nf(c.cong).replace(',0', '') + '/' + c.std, 'HANHLTM', '01/Nov/2026 09:00', 'Công'],
  ] as const;
  const ppl = [[c.u, 'Chính chủ · ' + c.seat], ['HANHLTM', '[HRD-MGR · kiêm] chốt công, tính lương'], ['PHILIP', '[ACC-CA] trống → [BOD-DIR] kiểm thay'], ['PHILIP', '[BOD-DIR] duyệt kỳ']] as const;
  const links = [[`Hồ sơ ${c.u}`, 'Lương hồ sơ · phiếu các kỳ'], [`Bảng lương kỳ ${PER}`, 'Về danh sách phiếu'], ['Nhật ký gửi phiếu', 'Tab Nhật ký gửi phiếu'], ['Bảng lương chuẩn', c.seat.split(' · ')[0] ?? '']] as const;

  return (
    <Page kind="table">
      <PageHeader
        title={`Phiếu lương · ${c.u}`}
        sub={`${c.seat} · kỳ ${PER}`}
        meta={<span className={`${s.st} ${s.stWarn}`}>Calculated</span>}
        findHint
        lead={
          <button type="button" className={s.tool} aria-label={`Về bảng lương kỳ ${PER}`} title={`Về bảng lương kỳ ${PER}`} onClick={onBack}>
            <IconArrowLeft size={18} aria-hidden="true" />
          </button>
        }
        actions={
          <>
            <div className={s.seg} role="group" aria-label="Mật độ">
              {(['Chuẩn', 'Gọn'] as const).map((l) => (
                <button key={l} type="button" aria-pressed={l === 'Chuẩn'} onClick={noWrite}>{l}</button>
              ))}
            </div>
            <HeaderTool label="Thông báo · 4 mới" icon={IconBell} onClick={noWrite} />
            <HeaderTool label="Thêm thao tác" icon={IconDots} onClick={noWrite} />
            <Button variant="primary" onClick={noWrite}>{t('pr.saveRecalc')}</Button>
          </>
        }
      />
      <div className={s.slip}>
        <div className={s.slipL}>
          <Card id="sum" title="Tóm tắt" open={open('sum')} toggle={toggle}>
            <div className={s.who}>
              <span className={s.av}>{ini(c.u)}</span>
              <div><b>{c.u}</b><div className={s.sub}>{c.seat} · Công {nf(c.cong).replace(',0', '')}/{c.std} · {att ? 'đủ điều kiện chuyên cần' : 'không đạt chuyên cần'}</div></div>
              <span className={`${s.st} ${s.stWarn}`}>Calculated</span>
            </div>
            <div className={s.sum3}>
              {sum.map(([k, v, tone]) => (
                <div key={k} data-tone={tone || undefined}><span>{k}</span><b>{v} <small>tr ₫</small></b></div>
              ))}
            </div>
            <div className={s.sbar} role="img" aria-label={`Gross ${nf(c.gross)} tr: thực nhận ${nf(c.net)}, bảo hiểm ${nf(c.ins)}, thuế và khấu trừ khác ${nf(c.pit + c.adv + c.oth)}`}>
              {bar.map(([k, v, col]) => <span key={k} title={`${k} · ${nf(v)} tr`} style={{ flex: Math.max(1, Math.round(v * 10)), background: col }} />)}
            </div>
          </Card>
          <Card id="inc" title="Thu nhập" open={open('inc')} toggle={toggle}>
            {inc.map(([k, v, note]) => (
              <div key={k} className={s.kv}><span>{k} {note && <em className={s.sub}>{note}</em>}</span><b>{nf(v)}</b></div>
            ))}
            {bon && <div className={s.kv}><span>{bon[0]} <em className={s.sub}>{t('pr.taxable')}</em></span><span className={s.rowAct}><b>{nf(bon[1])}</b><button type="button" className={s.del} aria-label={`Xoá khoản ${bon[0].toLowerCase()}`} onClick={noWrite}><IconX size={14} aria-hidden="true" /></button></span></div>}
            <div className={s.kv}><span>Gross</span><b>{nf(c.gross)}</b></div>
            <div className={s.bform}>
              <label>{t('pr.bonusWhy')}<input aria-label="Lý do thưởng" placeholder="VD: thưởng xử lý sự cố" /></label>
              <label>{t('pr.amountM')}<input aria-label="Số tiền thưởng" placeholder="0,5" inputMode="decimal" /></label>
              <label className={s.chk}><input type="checkbox" defaultChecked />{t('pr.taxableCap')}</label>
              <button type="button" className={s.miniBtn} onClick={noWrite}>{t('pr.add')}</button>
            </div>
          </Card>
          <Card id="ded" title="Khấu trừ & thực nhận" open={open('ded')} toggle={toggle}>
            {dedRows.map(([k, v, note]) => (
              <div key={k} className={s.kv}><span>{k} {note && <em className={s.sub}>{note}</em>}</span><b>{v ? `−${nf(v)}` : '—'}</b></div>
            ))}
            <div className={s.kv}><b>{t('pr.net')}</b><b>{nf(c.net)} <small>tr ₫</small></b></div>
          </Card>
          <Card id="act" title="Hoạt động" count={`${act.length} mục`} open={open('act')} toggle={toggle}>
            {act.map(([k, who, when, kind]) => (
              <div key={k} className={s.ppl}><span className={s.av}>{ini(who)}</span><div><b>{k}</b><div className={s.sub}>{who} · {when}</div></div><span className={s.kind}>{kind}</span></div>
            ))}
          </Card>
        </div>
        <div className={s.slipR}>
          <Card id="todo" title="Việc cần làm" count={String(todo.length)} open={open('todo')} toggle={toggle}>
            {todo.map((t) => (
              <div key={t.k} className={s.ppl}><button type="button" className={s.cb} aria-label={`Xong: ${t.k}`} onClick={noWrite} /><div><b>{t.k}</b><div className={s.sub}>{t.who} · {t.due}</div></div></div>
            ))}
          </Card>
          <Card id="info" title="Thông tin chính" open={open('info')} toggle={toggle}>
            {[['Người', c.u], ['Kỳ', PER], ['Người phụ thuộc', String(row[11])]].map(([k, v]) => (
              <div key={k} className={s.kv}><span>{k}</span>{k === 'Người' ? <a className={s.ext} href={`/mee/hr/profile/${v}`} onClick={(e) => { e.preventDefault(); noWrite(); }}>{v} ↗</a> : <b>{v}</b>}</div>
            ))}
          </Card>
          <Card id="steps" title="Các bước duyệt theo ghế" count={`${STEP}/6`} open={open('steps')} toggle={toggle}>
            <ol className={s.stepsV}>
              {PAY_STEPS.map(([k, sub], i) => (
                <li key={k} data-st={i < STEP ? 'done' : i === STEP ? 'cur' : 'todo'}>
                  <span className={s.dot}>{i < STEP ? '✓' : i + 1}</span>
                  <div>
                    <div className={s.sub}>{SEAT[i]![0]}</div>
                    <b>{SEAT[i]![1]}</b> <span className={s.sub}>{i < STEP ? 'Xong' : i === STEP ? 'Đang làm' : 'Chưa tới'} · {k} · {sub}</span>
                  </div>
                </li>
              ))}
            </ol>
          </Card>
          <Card id="ppl" title="Người phụ trách" open={open('ppl')} toggle={toggle}>
            {ppl.map(([u, role]) => (
              <div key={u + role} className={s.ppl}><span className={s.av}>{ini(u)}</span><div><b>{u}</b><div className={s.sub}>{role}</div></div><a className={s.hei} href="/hei/" aria-label={`Nhắn ${u} trong HANA Hei`} onClick={(e) => { e.preventDefault(); noWrite(); }}><IconMessage size={16} aria-hidden="true" /></a></div>
            ))}
          </Card>
          <Card id="links" title="Liên kết" open={open('links')} toggle={toggle}>
            {links.map(([k, sub]) => (
              <button key={k} type="button" className={s.lnk} onClick={k.startsWith('Bảng lương kỳ') ? onBack : noWrite}><b>{k}</b><span className={s.sub}>{sub}</span>›</button>
            ))}
          </Card>
        </div>
      </div>
    </Page>
  );
}
